import {
  PAY_BASIS_TO_EXPRESSED_AS,
  formatPayRangeLocationLabel,
} from "@components/PayRangeModal/payRangeDraftPayload";
import { archivedTag, rangeStatusBadge } from "@components/FairPay/payRangeBadges";
import {
  formatFairPayDate,
  formatFairPayVersion,
  formatFairPayVersionCode,
} from "@components/utils/fairPayFormat";

/** Backend status → table filter / menu key (the displayed label comes from payRangeBadges). */
export const API_STATUS_TO_UI = {
  DRAFT: "draft",
  PUBLISHED: "active",
  IN_FORCE: "active",
  RETIRED: "retired",
  ARCHIVED: "archived",
};

/** Table / chip status → Rule 18 list query values. */
export const UI_STATUS_TO_API = {
  draft: "DRAFT",
  active: "PUBLISHED",
  published: "PUBLISHED",
  retired: "RETIRED",
  archived: "ARCHIVED",
};

const WORK_MODE_LABEL = {
  ON_SITE: "On-Site",
  HYBRID: "Hybrid",
  REMOTE: "Remote",
  UNSPECIFIED: "Unspecified",
};

/**
 * List serializer returns country code but not flag URL.
 * Prefer any explicit flag fields; otherwise build from ISO country code
 * (same CDN used by CountryPicker / PayRanges storybook mocks).
 */
function resolveCountryFlag(country = {}) {
  const explicit =
    country.flag || country.icon || country.flag_icon || country.flag_url || "";
  if (explicit) return explicit;
  const code = String(country.code || "").trim().toLowerCase();
  if (!code || code.length !== 2) return "";
  return `https://flagcdn.com/${code}.svg`;
}

function toNumber(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatVersionDateLabel(isoDate, { kind = "as_of" } = {}) {
  const formatted = formatFairPayDate(isoDate);
  if (!formatted) return null;
  if (kind === "retired") return `Retired on ${formatted}`;
  if (kind === "through") return `through ${formatted}`;
  if (kind === "ended") return `ended on ${formatted}`;
  return `from ${formatted}`;
}

/**
 * Pay Range vs Readiness anchors (AC / PayRangeEditor):
 * readiness 0%–150% maps linearly onto min–max (range penetration 0%–100%).
 *   0% readiness   → min          (0% range penetration)
 *  50% readiness   → min + span/3 (33% range penetration)
 * 100% readiness   → min + 2span/3 (66% range penetration)
 * 150% readiness   → max          (100% range penetration)
 */
function buildAnchors(min, max) {
  const lo = toNumber(min);
  const hi = toNumber(max);
  if (lo == null || hi == null) {
    return {
      anchor_0: "-",
      anchor_50: "-",
      anchor_100: "-",
      anchor_150: "-",
    };
  }
  const span = Math.max(hi - lo, 0);
  return {
    anchor_0: lo,
    anchor_50: lo + span / 3,
    anchor_100: lo + (span * 2) / 3,
    anchor_150: hi,
  };
}

export const NO_VERSION_AT_DATE_LABEL = "No version at this date";
export const DRAFT_RANGE_VERSION_LABEL = "Draft V1 · not published yet";

/** The range's upcoming Future version (not voided, already numbered), or null. */
function findFutureVersion(versions) {
  if (!Array.isArray(versions)) return null;
  const future = versions
    .filter(
      (v) =>
        String(v?.state || "").toLowerCase() === "future" &&
        !v.voided_at &&
        v.version_number != null
    )
    .sort((a, b) => a.version_number - b.version_number)[0];
  return future
    ? {
        id: future.id,
        version_number: future.version_number,
        effective_from: future.effective_from || null,
      }
    : null;
}

/** Identity label "Job family › L{level} {Level name}"; null when the level is not sent. */
function buildIdentityLabel(apiRow) {
  const level = apiRow.job_family_level;
  const familyName = level?.job_family?.name;
  if (!level || !familyName || level.ordinal == null || !level.name) return null;
  return `${familyName} › L${level.ordinal} ${level.name}`;
}

function normalizeVersionState(state) {
  const key = String(state || "").toLowerCase();
  return ["current", "future", "lapsed"].includes(key) ? key : null;
}

/**
 * The version a row's Version cell describes: the snapshot's own version when the list sent
 * one, otherwise (Retired ranges are skipped by `as_of`) the latest non-voided version.
 */
function resolveDisplayedVersion(apiRow, snapshot) {
  const versions = Array.isArray(apiRow.versions) ? apiRow.versions : [];
  const live = versions.filter((v) => !v?.voided_at && v?.version_number != null);
  if (snapshot?.type === "version" && snapshot.version_number != null) {
    const match = live.find((v) => v.version_number === snapshot.version_number);
    const ended = ["RETIRED", "ARCHIVED"].includes(String(apiRow.status || "").toUpperCase());
    return {
      version_number: snapshot.version_number,
      state: normalizeVersionState(match?.state) || (ended ? "lapsed" : null),
      effective_from: snapshot.effective_from || match?.effective_from || null,
      effective_to: snapshot.effective_to || match?.effective_to || null,
    };
  }
  const statusKey = String(apiRow.status || "").toUpperCase();
  if (statusKey !== "RETIRED" && statusKey !== "ARCHIVED") return null;
  const last = [...live].sort((a, b) => b.version_number - a.version_number)[0];
  if (!last) return null;
  return {
    version_number: last.version_number,
    state: "lapsed",
    effective_from: last.effective_from || null,
    effective_to: last.effective_to || null,
  };
}

/**
 * Maps a PayRangeListSerializer payload → PayRangesTable row shape.
 * @param {object} apiRow
 * @param {{ asOfActive?: boolean, versionHistory?: boolean, asOfPast?: boolean }} [options]
 *   asOfActive: the list was requested with a Rule 18 `as_of`, so a non-DRAFT row without a
 *   version means "no version at that date".
 *   versionHistory: idea-MDP-429 is on — the row carries the pending Draft / Future version
 *   presentation and the state-based menus.
 *   asOfPast: the as-of date is before today — the draft line and pill are hidden.
 * @returns {object}
 */
export function mapPayRangeListRow(
  apiRow = {},
  { asOfActive = false, versionHistory = false, asOfPast = false } = {}
) {
  const snapshot = apiRow.compensation_snapshot || null;
  const statusKey = String(apiRow.status || "").toUpperCase();
  const uiStatus = API_STATUS_TO_UI[statusKey] || String(apiRow.status || "").toLowerCase();
  const noVersionAtDate = asOfActive && !snapshot && statusKey !== "DRAFT";

  const min = noVersionAtDate ? null : snapshot?.min_amount ?? apiRow.draft_min_amount ?? null;
  const max = noVersionAtDate ? null : snapshot?.max_amount ?? apiRow.draft_max_amount ?? null;
  const anchors = buildAnchors(min, max);
  const refAnchors = noVersionAtDate
    ? buildAnchors(null, null)
    : buildAnchors(apiRow.min_amount_in_reference, apiRow.max_amount_in_reference);

  const country = apiRow.operating_country || {};
  const currency = apiRow.operating_currency || {};
  const locationLabel = formatPayRangeLocationLabel(apiRow);
  const region =
    apiRow.operating_region ||
    apiRow.location_region?.display_label ||
    apiRow.location_region?.name ||
    "";

  let version_label = null;
  let version_date = null;
  let version_date_label = null;
  if (snapshot?.type === "version" && snapshot.version_number != null) {
    version_label = formatFairPayVersion(snapshot.version_number);
    version_date = snapshot.effective_from || null;
    const ended = uiStatus === "retired" || uiStatus === "archived";
    const dateKind = uiStatus === "retired" ? "retired" : ended ? "ended" : "as_of";
    version_date_label = formatVersionDateLabel(
      ended ? snapshot.effective_to || snapshot.effective_from : snapshot.effective_from,
      { kind: dateKind }
    );
  }

  const pendingDraft =
    versionHistory && apiRow.pending_draft_version
      ? {
          id: apiRow.pending_draft_version.id,
          effective_from: apiRow.pending_draft_version.effective_from || null,
        }
      : null;
  const futureVersion =
    versionHistory && !pendingDraft ? findFutureVersion(apiRow.versions) : null;
  const showDraftIndicators = !!pendingDraft && !asOfPast;
  const draftDate = showDraftIndicators ? formatFairPayDate(pendingDraft.effective_from) : null;
  const draft_line_label = showDraftIndicators
    ? draftDate
      ? `Draft: from ${draftDate}`
      : "Draft"
    : null;
  const futureDate = futureVersion && !asOfPast ? formatFairPayDate(futureVersion.effective_from) : null;
  const next_line_label =
    futureVersion && !asOfPast
      ? `Next: ${formatFairPayVersion(futureVersion.version_number)}${futureDate ? ` from ${futureDate}` : ""}`
      : null;

  let version_state = null;
  if (versionHistory && statusKey === "DRAFT") {
    version_label = formatFairPayVersionCode(apiRow.code);
    version_date_label = DRAFT_RANGE_VERSION_LABEL;
  } else if (versionHistory) {
    const displayed = resolveDisplayedVersion(apiRow, snapshot);
    if (displayed) {
      version_label = formatFairPayVersionCode(apiRow.code, displayed.version_number);
      version_state = displayed.state;
      version_date = displayed.effective_from;
      version_date_label =
        displayed.state === "lapsed"
          ? formatVersionDateLabel(displayed.effective_to, { kind: "through" })
          : formatVersionDateLabel(displayed.effective_from);
    }
  }

  const identityLabel = versionHistory ? buildIdentityLabel(apiRow) : null;
  const positionsEligible = uiStatus === "active";

  return {
    id: apiRow.id,
    code: apiRow.code,
    // ER-11730 retired the stored `name`; the list serializer now sends the live
    // Job Family Level label as `pay_range_name`. Keep the legacy fallback.
    name: identityLabel || apiRow.pay_range_name || apiRow.name,
    country_flag: resolveCountryFlag(country),
    operating_country: country.name || "",
    operating_country_id: country.id ?? null,
    region: locationLabel.includes(" - ")
      ? locationLabel.split(" - ").slice(1).join(" - ") || "Country Wide"
      : region || "Country Wide",
    currency: currency.code || "",
    operating_currency_id: currency.id ?? null,
    pay_basis: PAY_BASIS_TO_EXPRESSED_AS[apiRow.pay_basis] || apiRow.pay_basis || "",
    work_mode:
      apiRow.work_mode && String(apiRow.work_mode).toUpperCase() !== "UNSPECIFIED"
        ? WORK_MODE_LABEL[apiRow.work_mode] || apiRow.work_mode || ""
        : "",
    ...anchors,
    ref_anchor_0: refAnchors.anchor_0,
    ref_anchor_50: refAnchors.anchor_50,
    ref_anchor_100: refAnchors.anchor_100,
    ref_anchor_150: refAnchors.anchor_150,
    positions_in_use: apiRow.positions_in_use,
    status: uiStatus,
    status_badge: rangeStatusBadge(apiRow),
    archived_tag: archivedTag(apiRow),
    version_label,
    version_date,
    version_date_label,
    version_state,
    version_empty_label: noVersionAtDate ? NO_VERSION_AT_DATE_LABEL : null,
    version_history: versionHistory,
    pending_draft: pendingDraft,
    future_version: futureVersion,
    draft_line_label,
    next_line_label,
    show_draft_pending_pill: showDraftIndicators,
    muted: versionHistory && (uiStatus === "retired" || uiStatus === "archived"),
    positions_query: versionHistory && positionsEligible ? { payRange: apiRow.code } : null,
  };
}

/**
 * @param {string[]} uiStatuses
 * @returns {string[]}
 */
export function mapUiStatusesToApi(uiStatuses = []) {
  return uiStatuses
    .map((s) => UI_STATUS_TO_API[String(s).toLowerCase()] || String(s).toUpperCase())
    .filter(Boolean);
}
